import { FiUsers } from "react-icons/fi";
import SkeletonBase from "newui/components/Skeleton";
import type { EvaluationPanelMember } from "../../../types/panel.type";

interface PanelMemberSummaryProps {
	currentUserId: number | null;
	/** While the panel is still loading (and no members yet), show a loader
	 * instead of the "no members" empty state. */
	isLoading?: boolean;
	members: EvaluationPanelMember[];
}

export const PanelMemberSummary: React.FC<PanelMemberSummaryProps> = ({
	members,
	currentUserId,
	isLoading = false,
}) => {
	const activeMembers = members.filter((m) => m.status !== "revoked");
	const humanMembers = activeMembers.filter((m) => m.member_type !== "ai");
	const aiMembers = activeMembers.filter((m) => m.member_type === "ai");
	const isCurrentUserMember = currentUserId !== null && activeMembers.some(
		(m) => m.user_id === currentUserId
	);

	const total = activeMembers.length;

	// Show a loader while the panel is still loading and we have nothing yet,
	// rather than briefly flashing the "no members" empty state.
	if (total === 0 && isLoading) {
		return (
			<div
				className="space-y-3"
				data-testid="panel-member-summary-loading"
				aria-busy="true"
				aria-live="polite"
			>
				<SkeletonBase startColor="lightgray" width="100%" height="72px" className="!rounded-lg" />
			</div>
		);
	}

	if (total === 0) {
		return (
			<div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400">
				No panel members have been added yet.
			</div>
		);
	}

	const otherCount = isCurrentUserMember ? total - 1 : total;

	return (
		<div
			className="rounded-lg border border-gray-200 bg-white p-5"
			data-testid="panel-member-summary"
		>
			<div className="flex items-center gap-3">
				<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50">
					<FiUsers size={20} className="text-[#0031DD]" />
				</div>
				<div>
					<p className="text-sm font-medium text-gray-900">
						{isCurrentUserMember
							? otherCount > 0
								? `You + ${otherCount} additional member${otherCount !== 1 ? "s" : ""}`
								: "You are the only panel member"
							: `${total} panel member${total !== 1 ? "s" : ""}`
						}
					</p>
					{aiMembers.length > 0 && (
						<p className="mt-0.5 text-xs text-gray-500">
							{humanMembers.length} human
							<span className="mx-1">·</span>
							{aiMembers.length} AI
						</p>
					)}
				</div>
			</div>
		</div>
	);
};
